/**
 * O que os cartões "Folha de Pagamento" e "Lançamentos de Outros Pagamentos" do
 * Fechamento somam e ordenam.
 *
 * Papel: aqui ficam `folhaDoMes`, que junta as folhas fechadas do mês num cartão
 * só (`renderFolhaGapCard`, l. 3460–3500), `resumoDosOutrosPagamentos`, que
 * soma os lançamentos de outros bancos por categoria (`calcOutrosBancosMes`,
 * l. 4069–4080), e `porValorDecrescente`, a ordem dos itens de todos os cartões
 * de folha.
 *
 * Como usar: a ordem é do maior para o menor, e o empate fica na ordem em que
 * os itens chegaram — a ordem do `sort` estável da origem; uma ordem que
 * desempatasse pelo nome mudaria a posição de linhas de mesmo valor.
 */
import {
  type FolhaFechada,
  type ItemDeFolhaFechada,
  type LancamentoDeOutrosBancos,
  totalArredondado,
} from '@concierge/shared';

/**
 * Os itens do maior para o menor valor, estável no empate.
 *
 * @param itens - Os itens na ordem em que chegaram.
 * @returns Uma cópia ordenada.
 */
export function porValorDecrescente<Item extends { valor: number }>(
  itens: readonly Item[],
): Item[] {
  return [...itens].sort((a, b) => b.valor - a.valor);
}

/** O cartão "Folha de Pagamento" do mês. */
export interface FolhaDoMes {
  titulo: string;
  itens: ItemDeFolhaFechada[];
  total: number;
}

/**
 * Junta as folhas fechadas do mês, de todos os favorecidos, num cartão só.
 *
 * @param folhas - As folhas, na ordem em que foram fechadas pela primeira vez.
 * @returns O título da primeira folha (ou "Folha de Pagamento"), os itens de
 *   todas em ordem decrescente e o total arredondado; `null` sem folha.
 */
export function folhaDoMes(folhas: readonly FolhaFechada[]): FolhaDoMes | null {
  const [primeira] = folhas;
  if (primeira === undefined) return null;
  const itens = folhas.flatMap((folha) => folha.itens);
  return {
    titulo: primeira.titulo === '' ? 'Folha de Pagamento' : primeira.titulo,
    itens: porValorDecrescente(itens),
    total: totalArredondado(itens),
  };
}

/** O cartão "Lançamentos de Outros Pagamentos" do mês. */
export interface ResumoDosOutrosPagamentos {
  despesas: number;
  entradas: number;
  porCategoria: { categoria: string; valor: number }[];
}

/**
 * Soma os lançamentos de outros bancos do mês.
 *
 * @param lancamentos - Os lançamentos do mês aberto.
 * @returns A soma das despesas e das entradas, sem arredondar, e as despesas
 *   por categoria (a vazia vira "Outros") em ordem decrescente; `null` sem
 *   lançamento nenhum. O mês só com entradas tem resumo, com as despesas em
 *   zero, e o cartão aparece.
 */
export function resumoDosOutrosPagamentos(
  lancamentos: readonly LancamentoDeOutrosBancos[],
): ResumoDosOutrosPagamentos | null {
  if (lancamentos.length === 0) return null;
  let despesas = 0;
  let entradas = 0;
  const porCategoria = new Map<string, number>();
  for (const lancamento of lancamentos) {
    if (lancamento.tipo === 'receita') {
      entradas += lancamento.valor;
      continue;
    }
    despesas += lancamento.valor;
    const categoria = lancamento.categoria === '' ? 'Outros' : lancamento.categoria;
    porCategoria.set(categoria, (porCategoria.get(categoria) ?? 0) + lancamento.valor);
  }
  return {
    despesas,
    entradas,
    porCategoria: porValorDecrescente(
      [...porCategoria].map(([categoria, valor]) => ({ categoria, valor })),
    ),
  };
}
